import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Target, Calendar, CheckCircle, Clock, TrendingUp, Lightbulb, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SmartGoalView() {
  const { goalId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadGoal();
  }, [user, goalId]);

  const loadGoal = async () => {
    try {
      const response = await axios.get(`${API}/study-buddy/smart-goals/${goalId}`);
      setGoal(response.data);
    } catch (error) {
      toast.error('Failed to load goal');
      navigate('/study-buddy');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSession = async (eventId) => {
    const isCompleted = goal.completed_sessions?.includes(eventId);
    
    try {
      const updatedSessions = isCompleted
        ? goal.completed_sessions.filter(id => id !== eventId)
        : [...(goal.completed_sessions || []), eventId];
      
      const progress = Math.round((updatedSessions.length / goal.calendar_events.length) * 100);
      
      await axios.put(
        `${API}/study-buddy/smart-goals/${goalId}/progress`,
        null,
        {
          params: {
            progress_percentage: progress,
            completed_session_id: eventId
          }
        }
      );
      
      setGoal(prev => ({
        ...prev,
        completed_sessions: updatedSessions,
        progress_percentage: progress
      }));
      
      toast.success(isCompleted ? 'Session unmarked' : 'Session completed! 🎉');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this S.M.A.R.T. Goal? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/study-buddy/smart-goals/${goalId}`);
      toast.success('Goal deleted successfully');
      navigate(`/study-buddy/course/${goal.course_id}`);
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/study-buddy/course/${goal.course_id}`)}
              className="text-[#2C1810] hover:text-[#E85D4D] font-semibold mb-4 flex items-center gap-2"
            >
              ← Back to Course
            </button>
            
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Target className="w-8 h-8 text-[#E8934D]" />
                  <h1 className="text-4xl font-bold retro-title text-[#2C1810]">
                    S.M.A.R.T. Goal
                  </h1>
                </div>
                
                <p className="text-xl text-gray-700 mb-4">{goal.goal_summary}</p>
                
                {/* Progress Bar */}
                <div className="max-w-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-gray-700">Progress</span>
                    <span className="text-sm text-gray-600">{goal.progress_percentage}% Complete</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${goal.progress_percentage}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {goal.completed_sessions?.length || 0} of {goal.calendar_events?.length || 0} sessions completed
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleDelete}
                className="p-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                title="Delete Goal"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Stats */}
            <div className="lg:col-span-1 space-y-6">
              {/* Total Hours */}
              <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-[#E8934D]" />
                  <h2 className="text-xl font-bold text-[#2C1810]">Total Time</h2>
                </div>
                <p className="text-4xl font-bold text-[#E8934D]">{goal.total_hours_required}</p>
                <p className="text-sm text-gray-600">hours required</p>
              </div>

              {/* Study Tips */}
              {goal.study_tips && goal.study_tips.length > 0 && (
                <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Lightbulb className="w-6 h-6 text-[#E8934D]" />
                    <h2 className="text-xl font-bold text-[#2C1810]">Study Tips</h2>
                  </div>
                  <ul className="space-y-2">
                    {goal.study_tips.map((tip, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-[#E8934D]">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Milestones */}
              {goal.milestones && goal.milestones.length > 0 && (
                <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <TrendingUp className="w-6 h-6 text-[#E8934D]" />
                    <h2 className="text-xl font-bold text-[#2C1810]">Milestones</h2>
                  </div>
                  <div className="space-y-3">
                    {goal.milestones.map((milestone, index) => (
                      <div key={index} className="border-l-4 border-[#E8934D] pl-4 py-2">
                        <p className="font-bold text-[#2C1810]">{milestone.title}</p>
                        <p className="text-sm text-gray-600">{milestone.target_date}</p>
                        {milestone.deliverable && (
                          <p className="text-sm text-gray-700 mt-1">{milestone.deliverable}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Schedule */}
            <div className="lg:col-span-2">
              <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-6 h-6 text-[#E8934D]" />
                  <h2 className="text-2xl font-bold text-[#2C1810]">Study Schedule</h2>
                </div>

                {goal.calendar_events && goal.calendar_events.length > 0 ? (
                  <div className="space-y-4">
                    {goal.calendar_events
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((event, index) => {
                        const isCompleted = goal.completed_sessions?.includes(event.title + event.date);
                        const eventDate = moment(event.date);
                        const isPast = eventDate.isBefore(moment(), 'day');
                        const isToday = eventDate.isSame(moment(), 'day');
                        const isFuture = eventDate.isAfter(moment(), 'day');
                        
                        return (
                          <div
                            key={index}
                            className={`border-2 rounded-lg p-4 transition-all ${
                              isCompleted
                                ? 'bg-green-50 border-green-500'
                                : isPast
                                ? 'bg-red-50 border-red-300'
                                : isToday
                                ? 'bg-[#E8934D]/10 border-[#E8934D]'
                                : 'bg-white border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <button
                                    onClick={() => handleToggleSession(event.title + event.date)}
                                    className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                      isCompleted
                                        ? 'bg-green-500 border-green-500'
                                        : 'border-gray-400 hover:border-[#E8934D]'
                                    }`}
                                  >
                                    {isCompleted && <CheckCircle className="w-4 h-4 text-white" />}
                                  </button>
                                  
                                  <h3 className={`font-bold ${isCompleted ? 'line-through text-gray-500' : 'text-[#2C1810]'}`}>
                                    {event.title}
                                  </h3>
                                </div>
                                
                                <div className="ml-8 space-y-1">
                                  <p className="text-sm text-gray-700">
                                    📅 {eventDate.format('dddd, MMMM D, YYYY')}
                                    {isToday && <span className="ml-2 text-[#E85D4D] font-bold">• Today!</span>}
                                    {isPast && !isCompleted && <span className="ml-2 text-red-600 font-bold">• Overdue</span>}
                                  </p>
                                  
                                  {event.start_time && (
                                    <p className="text-sm text-gray-600">
                                      🕐 {event.start_time} ({event.duration_minutes} minutes)
                                    </p>
                                  )}
                                  
                                  {event.tasks && event.tasks.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-sm font-bold text-gray-700">Tasks:</p>
                                      <ul className="text-sm text-gray-600 list-disc list-inside">
                                        {event.tasks.map((task, i) => (
                                          <li key={i}>{task}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No study sessions scheduled</p>
                  </div>
                )}
              </div>

              {/* Weekly Schedule */}
              {goal.weekly_schedule && goal.weekly_schedule.length > 0 && (
                <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6 mt-6">
                  <h2 className="text-2xl font-bold text-[#2C1810] mb-4">Weekly Breakdown</h2>
                  <div className="space-y-4">
                    {goal.weekly_schedule.map((week, index) => (
                      <div key={index} className="border-2 border-gray-300 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-[#2C1810]">Week {week.week}</h3>
                          <span className="text-sm text-gray-600">{week.hours} hours</span>
                        </div>
                        <p className="text-sm text-[#E8934D] font-bold mb-2">{week.focus}</p>
                        {week.tasks && (
                          <ul className="text-sm text-gray-700 space-y-1">
                            {week.tasks.map((task, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-[#E8934D]">•</span>
                                <span>{task}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
